/**
 * contactsView.ts — pure view logic for the Contacts page (redesign slice 5, the
 * calm-minimal Contacts board `docs/design/calm-minimal/Contacts.dc.html`;
 * ADOPTION.md §17, register row "Redesign slice 5"). Component-free so vitest
 * pins every rule. Every figure derives from a read path the page already has:
 * the contacts list (capped at 200 — the table displays exactly those rows) and
 * the server's per-status totals on /v1/sales/stats (the TRUE counts beyond the
 * cap — the sidebar chip's own source, so header and chip cannot disagree).
 */
import type { SalesContact } from '../../types'
import type { ContactStats } from '../../../../api/types'

export const STATUSES = ['new', 'contacted', 'qualified', 'unqualified'] as const
export type ContactStatusKey = typeof STATUSES[number]

/** The status segmented control's counts. From the STATS ROUTE when it
 *  answered (true totals past the cap; refetched after every inline status
 *  change so they stay live), else from the loaded list — exact whenever the
 *  list is not capped, and the page's pre-slice behaviour. `source` says which. */
export function statusCounts(stats: ContactStats | null | undefined, contacts: readonly SalesContact[]): { all: number; new: number; contacted: number; qualified: number; unqualified: number; source: 'stats' | 'list' } {
  if (stats) return { all: stats.total, new: stats.new, contacted: stats.contacted, qualified: stats.qualified, unqualified: stats.unqualified, source: 'stats' }
  const n = (s: ContactStatusKey) => contacts.filter(c => c.status === s).length
  return { all: contacts.length, new: n('new'), contacted: n('contacted'), qualified: n('qualified'), unqualified: n('unqualified'), source: 'list' }
}

/** The title row's three figures: PEOPLE is the stats route's total (null
 *  until it answers — rendered "—", never the capped list's length);
 *  CURRENT PROSPECTS = the loaded rows whose segment is `current` (the same
 *  field the Segment filter reads); LINKED TO DEALS = the loaded rows with a
 *  server deal count above 0. The two list-derived figures describe the
 *  loaded rows and say so through the cap sentence when the list is capped. */
export function headFigures(stats: ContactStats | null | undefined, contacts: readonly SalesContact[]): { people: number | null; currentProspects: number; linkedToDeals: number } {
  return {
    people: stats ? stats.total : null,
    currentProspects: contacts.filter(c => c.prospectSegment === 'current').length,
    linkedToDeals: contacts.filter(c => (c.deals ?? 0) > 0).length,
  }
}

export type EmailState = 'ok' | 'invalid' | 'none'
/** A SHAPE check, not deliverability: one "@" with something on each side and
 *  a dot in the domain — "jbond.co.uk" is INVALID (no "@"), an empty address
 *  is NONE ("Add email"). Named "Invalid" on the board; the rule is this one. */
export function emailState(email: string): EmailState {
  const e = (email ?? '').trim()
  if (!e) return 'none'
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? 'ok' : 'invalid'
}

export function unassigned(c: Pick<SalesContact, 'assignedTo'>): boolean {
  return !(c.assignedTo ?? '').trim()
}
/** "9 have no owner" — the loaded rows without an owner. */
export function noOwnerCount(contacts: readonly SalesContact[]): number {
  return contacts.filter(unassigned).length
}
export function noOwnerLabel(n: number): string {
  return `${n} ${n === 1 ? 'has' : 'have'} no owner`
}
/** The Owner chip's names: every owner on the loaded rows, once, sorted. */
export function ownerNames(contacts: readonly SalesContact[]): string[] {
  return [...new Set(contacts.filter(c => !unassigned(c)).map(c => c.assignedTo!.trim()))].sort((a, b) => a.localeCompare(b))
}

export interface ContactsFilters { status: string; segment: string; owner: string; noOwnerOnly: boolean; search: string }
/** The status and segment filters are the saved view's (they persist with a
 *  view); owner, the attention toggle and the search are session-local.
 *  `owner` '' = anyone, '—' = unassigned, else an owner's name. */
export function applyContactsFilters(contacts: readonly SalesContact[], f: ContactsFilters): SalesContact[] {
  const q = f.search.trim().toLowerCase()
  return contacts.filter(c => {
    if (f.status !== 'all' && c.status !== f.status) return false
    if (f.segment !== 'all' && c.prospectSegment !== f.segment) return false
    if (f.owner === '—' ? !unassigned(c) : f.owner !== '' && (c.assignedTo ?? '').trim() !== f.owner) return false
    if (f.noOwnerOnly && !unassigned(c)) return false
    if (q && !c.name.toLowerCase().includes(q) && !c.company.toLowerCase().includes(q) && !c.email.toLowerCase().includes(q)) return false
    return true
  })
}

/** "Showing 10 of 10 contacts" — the total is the STATS route's when it
 *  answered, else the loaded count; when the list IS capped (200 rows loaded
 *  and the route counts more) the cap sentence family says so
 *  (ReportsDrillView's wording). A shortfall below the cap is not the cap's
 *  doing and gets no sentence — the two numbers show it. */
export const LIST_CAP = 200
export function showingLine(shown: number, total: number | null, loaded: number): string {
  const y = total ?? loaded
  const capped = total != null && loaded >= LIST_CAP && loaded < total
  return `Showing ${shown} of ${y} contacts${capped ? ' — list calls cap at 200.' : ''}`
}
/** "0 enriched" — TRUE (nothing writes the LinkedIn columns) and stays; the
 *  spec's "last contacted not yet tracked" is FALSE and never rendered (§12.7). */
export function enrichedLine(contacts: readonly SalesContact[]): string {
  return `${contacts.filter(c => c.enriched).length} enriched`
}

export function initials(name: string): string {
  return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
}

/** The drawer's LinkedIn row: the STORED URL or "Not linked" — never a
 *  fabricated `linkedin.com/in/<name>` (the pre-slice defect: it built an
 *  address from the person's name whenever the enriched flag was set). */
export function linkedinLine(c: Pick<SalesContact, 'linkedinUrl'>): string {
  const u = (c.linkedinUrl ?? '').trim()
  return u ? u : 'Not linked'
}
